import { useEffect, useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  EnrollmentTaxFilingStatusType,
  MaritalStatus,
  useGetEnrollmentJointFinancesData,
  useSubmitEnrollmentJointFinancesData,
} from '@egw/api'
import { Input, Button, Alert, AlertType, GenericError, RadioGroup, ProgressBar, Tag } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentJointLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { sendEvent } from '@egw/events'
import { WEALTH_ROUTES } from '@egw/constants'
import { UserIcon } from '@heroicons/react/16/solid'
import { useFlags } from '@egw/feature-flags'
import { type FeatureFlags } from '@egw/constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { netWorthRangeItems } from '../../../constants/confirm-personal-info/confirm-personal-info'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { EnrollmentPageLoading } from '../../../components/page-loading/page-loading'
import { JOINT_ACCOUNT_TYPE } from '../../../constants/joint-account-types'

const log = new Logger('Enrollment - Joint - Finances')

interface FormData {
  maritalStatus: MaritalStatus | undefined
  taxFilingStatus: EnrollmentTaxFilingStatusType | undefined
  income: string
  netWorth: string
}

export const FinancesPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const { flags } = useFlags<FeatureFlags>()
  const isV2Enabled = flags.rolloutJointEnrollmentV2

  const { data, isLoading, error } = useGetEnrollmentJointFinancesData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointFinancesData()
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors: formErrors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      maritalStatus: undefined,
      taxFilingStatus: undefined,
      income: '',
      netWorth: '',
    },
  })

  const taxFilingStatus = data?.activePerson?.taxFilingStatus ?? ''
  const personalAnnualGrossIncome = data?.activePerson?.personalAnnualGrossIncome ?? undefined
  const personalNetWorthRange = data?.activePerson?.personalNetWorthRange ?? ''
  const maritalStatus = data?.activePerson?.maritalStatus ?? undefined
  const accountType = data?.accountType ?? ''

  const isMaritalStatusDisabled =
    [JOINT_ACCOUNT_TYPE.JOINT_TENANTS_BY_THE_ENTIRETY, JOINT_ACCOUNT_TYPE.COMMUNITY_PROPERTY].includes(accountType) &&
    isV2Enabled

  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''
  const applicationId = data?.activePerson?.applicationId ?? ''
  const progress = data?.activePerson?.progress

  useEffect(() => {
    if (data?.activePerson) {
      reset({
        maritalStatus: isMaritalStatusDisabled ? MaritalStatus.MARRIED : maritalStatus,
        taxFilingStatus: (taxFilingStatus || undefined) as EnrollmentTaxFilingStatusType | undefined,
        income: personalAnnualGrossIncome
          ? formatInputToCurrency(personalAnnualGrossIncome.toString(), true)
          : undefined,
        netWorth: personalNetWorthRange,
      })
    }
  }, [
    data,
    reset,
    taxFilingStatus,
    personalAnnualGrossIncome,
    personalNetWorthRange,
    maritalStatus,
    isMaritalStatusDisabled,
  ])

  const income = watch('income')

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - Finances')

  useEffect(() => {
    if (!hasShownIntent && income) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_INTENT, {
        applicationId,
        annualIncome: income,
      })
    }
  }, [applicationId, hasShownIntent, income])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid && formData.taxFilingStatus) {
      try {
        const response = await mutate({
          activePerson: {
            maritalStatus: formData.maritalStatus,
            taxFilingStatus: formData.taxFilingStatus,
            personalAnnualGrossIncome: Number(removeCurrencySymbols(formData.income.toString())) || null,
            personalNetWorthRange: formData.netWorth,
          },
        })

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_SUBMIT, {
          applicationId,
          annualIncome: formData.income,
        })
        // If path exists and is different
        if (response?.activePerson?.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_VERIFIED, {
            applicationId,
            annualIncome: formData.income,
          })
          navigate(response.activePerson.path)
        } else if (!response?.activePerson?.path) {
          // If no path from BE, redirect to error
          log.error('No path returned from backend, redirecting to error page')
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      } catch (e: unknown) {
        log.error('Error submitting finances form', e)
      }
    }
  })

  return (
    <EnrollmentJointLayout title={t('joint.finances.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="finances-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            title={t('joint.finances.title')}
            className="mb-sm"
            // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/employment`}
          />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
          <p className="typo-primary-base text-text-meta mb-xl">{t('joint.finances.description')}</p>

          <p className="typo-primary-base-semibold text-text-header mb-sm">{t('edit_fields.marital_status_title')}</p>
          <Controller
            name="maritalStatus"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                options={Object.values(MaritalStatus).map((item) => ({
                  value: item,
                  label: t(`marital_status.${item}`),
                }))}
                name="maritalStatus"
                className="pb-xs typo-primary-base mt-xs mb-xl"
                disabled={isMaritalStatusDisabled}
                testId="marital-status-radio"
              />
            )}
          />

          <p className="typo-primary-base-semibold text-text-header mb-sm">
            {t('joint.finances.tax_filing_status_title')}
          </p>
          <Controller
            name="taxFilingStatus"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref: _ref, ...restField } }) => (
              <RadioGroup
                {...restField}
                options={Object.values(EnrollmentTaxFilingStatusType).map((item) => ({
                  value: item,
                  label: t(`investments.filing-status.tax_filing_status.${item}`),
                }))}
                name="taxFilingStatus"
                className="pb-xs typo-primary-base mt-xs mb-xl"
                testId="tax-filing-status-radio"
              />
            )}
          />
          <p className="typo-primary-base-semibold text-text-header mb-sm">{t('joint.finances.annual_income_title')}</p>
          <p className="typo-primary-base-regular text-text-meta mb-sm">{t('joint.finances.annual_income_p1')}</p>
          <p className="typo-primary-base-regular text-text-meta mb-sm">{t('joint.finances.annual_income_p2')}</p>
          <p className="typo-primary-base-regular text-text-meta mb-md">{t('joint.finances.annual_income_p3')}</p>

          <Controller
            name="income"
            control={control}
            rules={{
              required: t('common.required_field'),
            }}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(e) => {
                  const { value } = e.target
                  let formattedValue =
                    formatInputToCurrency(value, true) === '0' ? '' : formatInputToCurrency(value, true) // format to currency
                  formattedValue = formattedValue.replaceAll('.', '') // remove cents
                  e.target.value = formattedValue // set internal value
                  field.onChange(e)
                  setValue('income', formattedValue)
                }}
                label={t('joint.finances.annual_income')}
                testId="annual-income-input"
                error={formErrors.income?.message}
                maxLength={12}
                isPII
              />
            )}
          />

          <p className="typo-primary-base-semibold text-text-header mt-xl mb-sm">
            {t('joint.finances.your_net_worth')}
          </p>

          <Controller
            name="netWorth"
            control={control}
            rules={{
              required: t('common.required_field'),
            }}
            render={({ field: { ref: _ref, ...restField } }) => (
              <RadioGroup
                {...restField}
                onChange={(e) => {
                  restField.onChange(e)
                }}
                options={netWorthRangeItems.map((item) => ({
                  value: item.value,
                  label: t(`joint.finances.personal_net_worth_range.${item.value}`),
                }))}
                name="personalNetWorthRange"
                className="pt-xs pb-xs"
                testId="net-worth-radio"
                isPII
              />
            )}
          />

          <div className="flex-1 md:hidden" />

          {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
          >
            {t('common.continue')}
          </Button>
        </form>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
